/**
 * capacity.js — the capacity/effort feed: learned per-type effort + the default/learned merge.
 *
 * Defaults live in the effort levers (scheduler_config grp 'Effort'); learned actuals come from
 * /api/capacity-learned. `effectiveEffortLevers` decides which the load model uses, gated by the
 * `use_learned_effort` switch and a minimum sample size so a thin learned average never overrides a
 * sane default prematurely.
 */

import { EFFORT_LEVER_KEYS } from './work.js'

/** Learned per-type effort: { [Type]: { hoursPerItem, items, entries } }. */
export async function getLearnedEffort() {
  const token = (typeof window !== 'undefined' && window.__clerkGetToken) ? await window.__clerkGetToken() : null
  const res = await fetch('/api/capacity-learned', {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
  return data.learned || {}
}

/**
 * Merge learned actuals into the effort levers when the `use_learned_effort` switch is on and a type
 * has enough samples. Returns a new lever map; the load model reads effort from it transparently.
 * @param {object} leverMapObj flat lever map (leverMap output)
 * @param {object} learned     getLearnedEffort() result
 */
export function effectiveEffortLevers(leverMapObj, learned) {
  const lv = { ...(leverMapObj || {}) }
  if (!lv.use_learned_effort) return lv
  const minN = Number(lv.learned_min_sample) || 4
  for (const [k, type] of Object.entries(EFFORT_LEVER_KEYS)) {
    const L = learned && learned[type]
    if (L && L.items >= minN && Number.isFinite(L.hoursPerItem)) lv[k] = L.hoursPerItem
  }
  return lv
}
